import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { AppContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import { getSupplierCreditsList } from '../../clients/supplier_credit_client';
import { getSupplierSelectList } from '../../clients/supplier_client';
import InfiniteScroll from 'react-infinite-scroller';
import { showToast } from '../../config_and_helpers/helpers';
import { Button, Spinner } from 'react-bootstrap';

import GenericSearchFilter from '../../components/Generic/GenericSearchFilter';
import SupplierCreditTable from '../../components/SupplierCredit/SupplierCreditTable';
import SupplierCreditCreateModal from '../../components/SupplierCredit/SupplierCreditCreateModal';
import GlassWorkspaceShell from '../../components/Glass/GlassWorkspaceShell';
import {
  logListSearchEvent,
  logListSearchError,
} from '../../config_and_helpers/logger';
import {
  abortListSearchController,
  getNextPageUrlFilterMismatch,
  isPartialListSearchQuery,
  isRunnableListSearchQuery,
  resetPaginationForPartialSearch,
  INFINITE_SCROLL_THRESHOLD_PX,
} from '../../utils/listSearchRequest';

const SupplierCreditsPage = () => {
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();

  const isMountedRef = useRef(false);
  const isLoadingRef = useRef(false);
  const debounceTimeoutRef = useRef(null);
  const currentRequestRef = useRef(null);
  const hasMoreRef = useRef(true);

  const [credits, setCredits] = useState([]);
  const [supplier, setSupplier] = useState('');
  const [supplierSelectList, setSupplierSelectList] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  const fetchCredits = useCallback(
    (
      {
        searchValue = '',
        nextPage = null,
        supplierId = '',
        sortByValue = 'created_at',
        sortDirectionValue = 'desc',
      } = {},
      newSearch = false
    ) => {
      const trigger = newSearch ? 'search-or-filter' : 'scroll-or-refresh';
      const params = {
        searchInput: searchValue,
        supplierId,
        nextPage,
        sortBy: sortByValue,
        sortDirection: sortDirectionValue,
      };

      if (currentRequestRef.current) {
        abortListSearchController(currentRequestRef);
      }

      setIsLoading(true);
      if (newSearch) {
        setIsSearching(true);
        setCredits([]);
      }
      isLoadingRef.current = true;

      const controller = new AbortController();
      currentRequestRef.current = controller;

      getSupplierCreditsList(token, setCredits, {
        searchInput: searchValue,
        supplierId,
        nextPage,
        sortBy: sortByValue,
        sortDirection: sortDirectionValue,
        signal: controller.signal,
      })
        .then(response => {
          if (currentRequestRef.current !== controller) {
            return;
          }

          if (response?.canceled) {
            isLoadingRef.current = false;
            setIsLoading(false);
            setIsSearching(false);
            currentRequestRef.current = null;
            return;
          }

          const responseMeta = {
            success: response?.success,
            reachedEnd: !!response?.reachedEnd,
            nextPage: response?.nextPage,
          };

          if (!isMountedRef.current) return;

          if (response?.success) {
            setNextPageUrl(response.reachedEnd ? null : response.nextPage);
            setHasMore(!response.reachedEnd);
            hasMoreRef.current = !response.reachedEnd;
          } else {
            showToast(
              'An unexpected error occurred. Please try again',
              'error',
              'top-center',
              3000
            );
          }

          logListSearchEvent(
            'supplier-credits',
            response?.success ? 'success' : 'failure',
            { trigger, params, responseMeta }
          );

          isLoadingRef.current = false;
          setIsLoading(false);
          setIsSearching(false);
          currentRequestRef.current = null;
        })
        .catch(error => {
          if (currentRequestRef.current !== controller) {
            return;
          }
          if (
            error?.name === 'AbortError' ||
            error?.code === 'ERR_CANCELED'
          ) {
            isLoadingRef.current = false;
            setIsLoading(false);
            setIsSearching(false);
            currentRequestRef.current = null;
            return;
          }

          if (!isMountedRef.current) return;

          logListSearchError('supplier-credits', trigger, error, { params });

          isLoadingRef.current = false;
          setIsLoading(false);
          setIsSearching(false);
          currentRequestRef.current = null;
        });
    },
    [token]
  );

  useEffect(() => {
    isMountedRef.current = true;
    // Always fetch the first page when the page mounts (or token changes),
    // so navigating back to this page refreshes the credits list.
    fetchCredits(
      { searchValue: '', nextPage: null, supplierId: '' },
      true
    );
    return () => {
      isMountedRef.current = false;
    };
  }, [token, fetchCredits]);

  useEffect(() => {
    getSupplierSelectList(token, setSupplierSelectList);
  }, [token]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (currentRequestRef.current) {
        abortListSearchController(currentRequestRef);
      }
    };
  }, []);

  const debouncedFetch = useCallback(
    (val, supplier, sortByValue, sortDirectionValue) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      if (val.length >= 2) {
        setCredits([]);
        setIsSearching(true);
      }

      if (isRunnableListSearchQuery(val)) {
        debounceTimeoutRef.current = setTimeout(() => {
          fetchCredits(
            {
              searchValue: val,
              nextPage: null,
              supplierId: supplier,
              sortByValue,
              sortDirectionValue,
            },
            true
          );
        }, 300);
      } else if (isPartialListSearchQuery(val)) {
        setCredits([]);
        setIsSearching(false);
        resetPaginationForPartialSearch({
          setHasMore,
          hasMoreRef,
          setNextPageUrl,
          abortControllerRef: currentRequestRef,
        });
      }
    },
    [fetchCredits]
  );

  const handleSearchInput = val => {
    setSearchInput(val);
    debouncedFetch(val, supplier, sortBy, sortDirection);
  };

  const handleSupplierChange = val => {
    setSupplier(val);
    fetchCredits(
      {
        searchValue: searchInput,
        nextPage: null,
        supplierId: val,
        sortByValue: sortBy,
        sortDirectionValue: sortDirection,
      },
      true
    );
  };

  const handleSortByChange = (value, direction = 'asc') => {
    setSortBy(value);
    setSortDirection(direction);
    fetchCredits(
      {
        searchValue: searchInput,
        nextPage: null,
        supplierId: supplier,
        sortByValue: value,
        sortDirectionValue: direction,
      },
      true
    );
  };

  return (
    <GlassWorkspaceShell theme="yellow" wide>
      <div className="glass-toolbar">
        <GenericSearchFilter
          entityType="suppliers"
          placeHolderLabel="Supplier"
          searchInput={searchInput}
          onSearchInput={handleSearchInput}
          onEntityChange={handleSupplierChange}
          entity={supplier}
          supplierSelectList={supplierSelectList}
          customActionButton={
            isViewer ? null : (
            <Button variant="success" onClick={() => setShowCreateModal(true)}>
              Create credit
            </Button>
            )
          }
        />
      </div>

      {(isLoading && credits.length === 0) || isSearching ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" />
          {isSearching && searchInput.length >= 2 && (
            <div className="mt-2 text-muted">Searching credits...</div>
          )}
        </div>
      ) : (
        <>
          {!isLoading && !isSearching && credits.length === 0 ? (
            <div className="text-center py-5">
              <h5 className="text-muted mb-2">No supplier credits found</h5>
              <p className="text-muted mb-3">
                {searchInput.length >= 2
                  ? `No credits match your search for "${searchInput}"`
                  : 'No credits available'}
              </p>
              {searchInput.length >= 2 && (
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => {
                    setSearchInput('');
                    fetchCredits(
                      {
                        searchValue: '',
                        nextPage: null,
                        supplierId: supplier,
                        sortByValue: sortBy,
                        sortDirectionValue: sortDirection,
                      },
                      true
                    );
                  }}
                >
                  Clear search
                </button>
              )}
            </div>
          ) : (
            <InfiniteScroll
              pageStart={0}
              loadMore={() => {
                if (
                  isLoadingRef.current ||
                  !nextPageUrl ||
                  isPartialListSearchQuery(searchInput)
                ) {
                  return;
                }

                const mismatch = getNextPageUrlFilterMismatch(nextPageUrl, {
                  search: searchInput,
                  supplier_id: supplier,
                  sort_by: sortBy,
                  sort_direction: sortDirection,
                });
                if (mismatch === null) {
                  logListSearchError(
                    'supplier-credits',
                    'next-page-url-stale-check',
                    new Error('Failed to parse nextPageUrl'),
                    { nextPageUrl, searchInput, supplier, sortBy, sortDirection }
                  );
                  setHasMore(false);
                  hasMoreRef.current = false;
                  setNextPageUrl(null);
                  return;
                }
                if (mismatch.mismatched) {
                  logListSearchEvent('supplier-credits', 'next-page-url-stale-mismatch', {
                    nextPageUrl,
                    expected: mismatch.expected,
                    actual: mismatch.actual,
                  });
                  setHasMore(false);
                  hasMoreRef.current = false;
                  setNextPageUrl(null);
                  return;
                }

                fetchCredits(
                  {
                    searchValue: searchInput,
                    nextPage: nextPageUrl,
                    supplierId: supplier,
                    sortByValue: sortBy,
                    sortDirectionValue: sortDirection,
                  },
                  false
                );
              }}
              hasMore={hasMore}
              loader={
                <div key="credit-loader" className="text-center py-2">
                  <Spinner animation="border" size="sm" role="status" />
                  <div className="mt-1">
                    <small className="text-muted">
                      Loading more credits...
                    </small>
                  </div>
                </div>
              }
              useWindow={true}
              threshold={INFINITE_SCROLL_THRESHOLD_PX}
            >
              <div className="glass-table-wrap">
              <SupplierCreditTable
                creditsList={credits}
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={handleSortByChange}
                onCreditUpdated={() =>
                  fetchCredits(
                    {
                      searchValue: searchInput,
                      supplierId: supplier,
                      sortByValue: sortBy,
                      sortDirectionValue: sortDirection,
                    },
                    true
                  )
                }
                handleDelete={() =>
                  fetchCredits(
                    {
                      searchValue: searchInput,
                      supplierId: supplier,
                      sortByValue: sortBy,
                      sortDirectionValue: sortDirection,
                    },
                    true
                  )
                }
              />
              </div>
            </InfiniteScroll>
          )}
        </>
      )}

      <SupplierCreditCreateModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onSuccess={() => {
          setShowCreateModal(false);
          fetchCredits(
            {
              searchValue: searchInput,
              supplierId: supplier,
              sortByValue: sortBy,
              sortDirectionValue: sortDirection,
            },
            true
          );
        }}
        supplierSelectList={supplierSelectList}
      />
    </GlassWorkspaceShell>
  );
};

export default SupplierCreditsPage;
